import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getTouchLike() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(hover: none), (pointer: coarse)").matches;
}

/**
 * HelpTooltip
 * - Desktop: hover/focus opens
 * - Mobile: tap toggles
 * - Closes on outside click or Escape
 * - Portal renders into document.body (prevents clipping in cards/modals)
 */
export default function HelpTooltip({
  content,
  placement = "top",
  maxWidthClass = "max-w-[280px]",
  ariaLabel = "Help",
  className = "",
}) {
  const id = useId();
  const tooltipId = `help-tooltip-${id}`;

  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [touchLike, setTouchLike] = useState(false);
  const [pos, setPos] = useState(null);

  const hasContent = useMemo(() => {
    if (content === null || content === undefined) return false;
    if (typeof content === "string") return content.trim().length > 0;
    return true;
  }, [content]);

  useEffect(() => {
    setTouchLike(getTouchLike());
    const onResize = () => setTouchLike(getTouchLike());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    const onPointerDown = (e) => {
      const t = e.target;
      if (!t) return;
      if (triggerRef.current && triggerRef.current.contains(t)) return;
      if (tooltipRef.current && tooltipRef.current.contains(t)) return;
      setOpen(false);
    };

    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("mousedown", onPointerDown, true);
    document.addEventListener("touchstart", onPointerDown, true);

    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("mousedown", onPointerDown, true);
      document.removeEventListener("touchstart", onPointerDown, true);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }

    const triggerEl = triggerRef.current;
    const tipEl = tooltipRef.current;
    if (!triggerEl || !tipEl) return;

    // Measure
    const r = triggerEl.getBoundingClientRect();

    // Make sure tooltip has layout before measuring
    const tr = tipEl.getBoundingClientRect();

    const margin = 10;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const centerX = r.left + r.width / 2;

    const left = clamp(centerX - tr.width / 2, margin, vw - tr.width - margin);

    const preferTop = placement === "top";
    const topForTop = r.top - tr.height - 10;
    const topForBottom = r.bottom + 10;

    // Flip if it would go offscreen.
    let top;
    let actualPlacement;
    if (preferTop) {
      if (topForTop >= margin) {
        top = topForTop;
        actualPlacement = "top";
      } else {
        top = clamp(topForBottom, margin, vh - tr.height - margin);
        actualPlacement = "bottom";
      }
    } else {
      if (topForBottom + tr.height <= vh - margin) {
        top = topForBottom;
        actualPlacement = "bottom";
      } else {
        top = clamp(topForTop, margin, vh - tr.height - margin);
        actualPlacement = "top";
      }
    }

    setPos({ left, top, placement: actualPlacement });
  }, [open, placement, content]);

  if (!hasContent) return null;

  const onClick = (e) => {
    // Prevent header buttons/etc from hijacking focus.
    e.stopPropagation();
    setOpen((v) => !v);
  };

  const onMouseEnter = () => {
    if (touchLike) return;
    setOpen(true);
  };

  const onMouseLeave = () => {
    if (touchLike) return;
    setOpen(false);
  };

  const onFocus = () => setOpen(true);
  const onBlur = () => setOpen(false);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-describedby={open ? tooltipId : undefined}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onFocus={onFocus}
        onBlur={onBlur}
        className={
          "inline-flex items-center justify-center rounded-full " +
          "h-7 w-7 text-inherit bg-black/5 border border-black/10 " +
          "dark:bg-white/10 dark:border-white/15 " +
          "hover:bg-black/10 dark:hover:bg-white/15 " +
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]/60 " +
          className
        }
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10Z"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M12 17v-5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <path
            d="M12 8.25h.01"
            stroke="currentColor"
            strokeWidth="3.2"
            strokeLinecap="round"
          />
        </svg>
      </button>

      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={tooltipRef}
            id={tooltipId}
            role="tooltip"
            className={
              "fixed z-[10000] " +
              maxWidthClass +
              " px-3 py-2 rounded-xl shadow-xl " +
              "border border-white/20 bg-white/20 backdrop-blur-xl text-sm text-white " +
              "dark:bg-black/40 dark:border-white/15"
            }
            style={{
              left: pos ? `${pos.left}px` : "-9999px",
              top: pos ? `${pos.top}px` : "-9999px",
            }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          >
            {/* arrow */}
            <div
              className={
                "absolute h-3 w-3 rotate-45 border border-white/20 bg-white/20 backdrop-blur-xl " +
                "dark:bg-black/40 dark:border-white/15"
              }
              style={
                pos?.placement === "top"
                  ? { left: "50%", bottom: "-6px", marginLeft: "-6px" }
                  : { left: "50%", top: "-6px", marginLeft: "-6px" }
              }
              aria-hidden="true"
            />

            <div className="leading-snug text-white/95">{content}</div>
          </div>,
          document.body
        )}
    </>
  );
}
